// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import baseTheme, { BaseTheme, ComponentThemeProps } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, get } from "@insite/mobius/utilities";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    MobiusStyledComponentPropsWithRef,
} from "@insite/mobius/utilities/MobiusStyledComponentProps";
import RecursivePartial from "@insite/mobius/utilities/RecursivePartial";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import xBreakpointMediaQueries from "@sc/XDL/Utilities/breakpointMediaQueries";
import merge from "lodash/merge";
import * as React from "react";
import styled, { css, ThemeProvider } from "styled-components";

export type PageProps = MobiusStyledComponentPropsWithRef<
    "main",
    { ref?: React.RefObject<HTMLElement | null> },
    {
        /** CSS value of the background. */
        background?: string;
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<PageProps>;
        /** The amount of padding inside the Page, in pixels. */
        padding?: number;
        /** Breakpoints at which the element should be full width. */
        fullWidth?: boolean[];
        /** This object, which partially matches the BaseTheme, modifies the theme for this page component and all its children */
        themeMod?: RecursivePartial<ComponentThemeProps>;
        /** This is for the compact header */
        hasCustomBackground?: boolean;
        isHeader?: boolean;
        isFooter?: boolean;
    }
>;

const PageStyle = styled.main<
    Pick<PageProps, "padding" | "fullWidth" | "isHeader" | "isFooter" | "background" | "hasCustomBackground">
>`
    display: block; /* needed for IE */
    margin: 0 auto;
    width: 100%;
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    position: relative; /* needed for Safari */
                `,
                css`
                    position: relative; /* needed for Safari */
                `,
            ],
            "max",
        )}

    background: ${({ background, theme, hasCustomBackground }) =>
        hasCustomBackground ? get(theme, background) : resolveColor(background, theme)};
    ${({ padding, theme, fullWidth }) => {
        let paddingValue = padding;
        const { maxWidths, containerPadding } = (theme as any).breakpoints || baseTheme.breakpoints;
        if (padding! > 100) {
            // eslint-disable-next-line no-console
            console.warn("Page: 'padding' property exceeds the maximum allowed value of 100.");
            paddingValue = 100;
        }

        const resolvedFullWith = fullWidth || [];

        return css`
            ${xBreakpointMediaQueries(theme, [
                resolvedFullWith[0]
                    ? css`
                          max-width: 100%;
                      `
                    : css`
                          max-width: ${maxWidths[0]}px;
                          padding: ${padding
                              ? `${paddingValue}px`
                              : (containerPadding && containerPadding[0]) || "0 16px"};
                      `,
                resolvedFullWith[1]
                    ? css`
                          max-width: 100%;
                      `
                    : css`
                          max-width: ${maxWidths[1]}px;
                          padding: ${padding
                              ? `${paddingValue}px`
                              : (containerPadding && containerPadding[1]) || "0 16px"};
                      `,
                resolvedFullWith[2]
                    ? css`
                          max-width: 100%;
                      `
                    : css`
                          max-width: ${maxWidths[2]}px;
                          padding: ${padding
                              ? `${paddingValue}px`
                              : (containerPadding && containerPadding[2]) || "0 16px"};
                      `,
                resolvedFullWith[3]
                    ? css`
                          max-width: 100%;
                      `
                    : css`
                          max-width: ${maxWidths[3]}px;
                          padding: ${padding
                              ? `${paddingValue}px`
                              : (containerPadding && containerPadding[3]) || "0 16px"};
                      `,
                resolvedFullWith[4]
                    ? css`
                          max-width: 100%;
                      `
                    : css`
                          max-width: ${maxWidths[4]}px;
                          padding: ${padding
                              ? `${paddingValue}px`
                              : (containerPadding && containerPadding[4]) || "0 16px"};
                      `,
            ])}
            @media print {
                max-width: 100%;
            }
        `;
    }}
    ${injectCss}
`;

/**
 * The Page component is a basic container whose maximum width is controlled by breakpoints defined in the theme,
 * just like the Grid component. It also allows for an arbitrary padding value.
 */
const Page: React.FC<PageProps> = ({
    background = "transparent",
    padding,
    fullWidth = [false, false, false, false, false],
    themeMod,
    hasCustomBackground,
    isHeader,
    isFooter,
    ...otherProps
}) => {
    return (
        <ThemeProvider
            theme={(theme: BaseTheme) => {
                if (themeMod) {
                    return merge(theme, themeMod);
                }
                return theme;
            }}
        >
            <PageStyle
                background={background}
                padding={padding}
                fullWidth={fullWidth}
                hasCustomBackground={hasCustomBackground}
                isHeader={isHeader}
                isFooter={isFooter}
                {...otherProps}
                data-test-selector={otherProps["data-test-selector"]}
            />
        </ThemeProvider>
    );
};

/** @component */
export default Page;

export { PageStyle };

